import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { BookOpen, Home, Share2 } from 'lucide-react';
import { CertificateView } from '../../components/certificate/CertificateView';
import { arcCertificate } from '../../components/certificate/certificateContent';

const Shell = ({ children }) => (
  <MainLayout><div className="mx-auto max-w-5xl space-y-6 p-6">{children}</div></MainLayout>
);

const ARCCertificate = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, cert: null, notCertified: false });

  const fetchCertificate = useCallback(async () => {
    setState({ loading: true, cert: null, notCertified: false });
    try {
      const response = await api.get('/arc/certificate');
      setState({ loading: false, cert: response.data, notCertified: false });
    } catch (error) {
      if (error.response?.status !== 400) toast.error('Failed to load certificate');
      setState({ loading: false, cert: null, notCertified: error.response?.status === 400 });
    }
  }, [api]);

  useEffect(() => { fetchCertificate(); }, [fetchCertificate]);

  const shareCertificate = async () => {
    const text = `I just earned my Advanced Recruitment Certification (ARC)! Certificate ID: ${state.cert.certificate_id}`;
    if (navigator.share) {
      navigator.share({ title: 'ARC Certification', text }).catch(() => {});
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Share text copied to clipboard');
    } catch {
      toast.error('Could not copy. Please allow clipboard access and try again.');
    }
  };

  if (state.loading) return <Shell><Skeleton className="h-12 w-64" /><Skeleton className="h-[600px]" /></Shell>;

  if (!state.cert) {
    return (
      <Shell>
        <Card className="mx-auto max-w-md p-8 text-center" data-testid="arc-certificate-unavailable">
          <BookOpen className="mx-auto mb-4 h-16 w-16 text-amber-500" />
          <h2 className="mb-2 text-2xl font-bold">{state.notCertified ? 'Not Yet Certified' : 'Unable to load certificate'}</h2>
          <p className="mb-6 text-muted-foreground">
            {state.notCertified ? 'Complete all chapters and pass the final exam to earn your certificate.' : 'Please try again.'}
          </p>
          <Button onClick={state.notCertified ? () => navigate('/arc') : fetchCertificate}>
            {state.notCertified ? 'Continue Learning' : 'Retry'}
          </Button>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      <Button variant="ghost" onClick={() => navigate('/arc')} data-testid="back-to-dashboard-btn">
        <Home className="mr-2 h-4 w-4" />Back to Dashboard
      </Button>
      <CertificateView
        data={arcCertificate(state.cert)}
        actions={(
          <Button variant="outline" onClick={shareCertificate} data-testid="share-certificate-btn">
            <Share2 className="mr-2 h-4 w-4" />Share
          </Button>
        )}
      />
    </Shell>
  );
};

export default ARCCertificate;
